import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { toast } from 'react-toastify';

export interface IOrgChartName {
  id: number;
  name: string;
  // Present only when the request asks for org_level sorting; the BE sorts
  // by hierarchy level (director→staff) and surfaces the level per node.
  org_level?: string | null;
  division_key?: string | null;
  level_key?: string | null;
}

interface OrgChartNamesParams {
  order_by?: 'org_level';
  order?: 'asc' | 'desc';
}

// Fetch org chart names. When params.order_by === 'org_level' the BE returns
// the list already sorted by org hierarchy level (and includes org_level per
// item); otherwise it falls back to its default nested-set order.
export const useGetOrgChartNames = (tenantId: string | number | undefined, params?: OrgChartNamesParams) => {
  return useQuery({
    queryKey: ['org-chart-names', tenantId, params?.order_by, params?.order],
    queryFn: async (): Promise<IOrgChartName[]> => {
      if (!tenantId) return [];

      const response = await axiosInstance.get<{ data: IOrgChartName[] }>(
        `${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/tenant/${tenantId}/names`,
        { params: params?.order_by ? { order_by: params.order_by, order: params.order ?? 'asc' } : undefined },
      );
      return response.data?.data || [];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
};

// Upload SOP media
export const useUploadSopMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sopId, file }: { sopId: number; file: File }) => {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('model_type', 'OrgSOP');
      formData.append('model_id', String(sopId));
      formData.append('collection_name', 'sop_documents');

      const response = await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/compro/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        transformRequest: [data => data], // Prevent axios from transforming FormData
      });

      return response.data;
    },
    onSuccess: () => {
      toast.success('File berhasil diunggah');
      queryClient.invalidateQueries({ queryKey: ['sops'] });
    },
    onError: (error: unknown) => {
      let errorMessage = 'Gagal mengunggah file';
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'data' in error.response &&
        typeof error.response.data === 'object' &&
        error.response.data !== null &&
        'message' in error.response.data
      ) {
        errorMessage = String(error.response.data.message);
      }
      console.error('Error uploading file:', error);
      toast.error(errorMessage);
    },
  });
};

// Delete SOP media
export const useDeleteSopMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mediaId }: { mediaId: number }) => {
      const response = await axiosInstance.delete(`${env.apiTasklistBaseUrl}/api/v1/compro/media/${mediaId}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('File berhasil dihapus');
      queryClient.invalidateQueries({ queryKey: ['sops'] });
    },
    onError: (error: unknown) => {
      let errorMessage = 'Gagal menghapus file';
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'data' in error.response &&
        typeof error.response.data === 'object' &&
        error.response.data !== null &&
        'message' in error.response.data
      ) {
        errorMessage = String(error.response.data.message);
      }
      console.error('Error deleting file:', error);
      toast.error(errorMessage);
    },
  });
};
